import React, { useState, useEffect } from 'react';
import {
  Card,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Badge,
  Alert,
  ProgressBar
} from 'react-bootstrap';
import api from '../../api/wbuoaApi';

const UserDashboard = () => {
  const [stats, setStats] = useState({
    documents: 0,
    categories: 0,
    galleryItems: 0,
    president: 1,
    secretary: 4,
    stateCommittee: 0,
    unitCommittee: 0,
    universities: 0,
    users: 0,
    loading: true,
    error: null,
    lastUpdated: null
  });

  const fetchDashboardData = async () => {
    setStats(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Make a single API call to the dashboard endpoint
      const response = await api.get('/users/dashboard');

      console.log('Dashboard API Response:', response.data);

      // Check if response has data directly or wrapped in data property
      let dashboardData = response.data;

      // If response has a data property (wrapped), use that
      if (response.data && response.data.data) {
        dashboardData = response.data.data;
      }

      // If response has status property, check if it's success
      if (response.data && response.data.status === 'error') {
        throw new Error(response.data.message || 'Failed to fetch dashboard data');
      }

      setStats({
        documents: dashboardData.documents || 0,
        categories: dashboardData.document_category || 0,
        galleryItems: dashboardData.gallery || 0,
        president: dashboardData.president || 1,
        secretary: dashboardData.secretary || 4,
        stateCommittee: dashboardData.statecommittee || 0,
        unitCommittee: dashboardData.unitcommittee || 0,
        universities: dashboardData.universities || 0,
        users: dashboardData.users || 0,
        loading: false,
        error: null,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Dashboard API error:', error);
      setStats(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message ||
          error.message ||
          'Failed to load dashboard data. Please check your API connection.'
      }));
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (stats.loading) {
    return (
      <Container className="py-5">
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
          <Spinner animation="border" variant="success" />
          <span className="mt-3 text-muted">Loading dashboard data...</span>
        </div>
      </Container>
    );
  }

  if (stats.error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>
            <i className="bi bi-exclamation-triangle me-2"></i>
            Error Loading Dashboard
          </Alert.Heading>
          <p>{stats.error}</p>
          <div className="d-flex justify-content-center mt-3">
            <Button
              variant="outline-danger"
              onClick={fetchDashboardData}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Retry Loading
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  const totalCommittee = stats.stateCommittee + stats.unitCommittee;
  const committeeData = [
    { name: 'State Committee', value: stats.stateCommittee, variant: 'success' },
    { name: 'Unit Committee', value: stats.unitCommittee, variant: 'info' }
  ];

  return (
    <div className="dashboard-container">
      <Container className="py-4">
        {/* Header Section */}
        <div className="dashboard-header text-center mb-4">
          <div className="header-logo mb-3">
            <div className="logo-icon d-inline-block p-3 bg-success rounded-circle mb-3">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1 className="text-success">Dashboard Overview</h1>
          </div>
          <p className="text-muted">
            <i className="bi bi-clock me-1"></i>
            Last updated: {stats.lastUpdated?.toLocaleString() || 'Never'}
            <Button
              variant="link"
              size="sm"
              onClick={fetchDashboardData}
              className="ms-2"
            >
              <i className="bi bi-arrow-clockwise"></i> Refresh
            </Button>
          </p>
        </div>

        {/* Stats Overview Cards */}
        <Row className="g-4 mb-4">
          <Col md={2} sm={4} xs={6}>
            <Card className="border-0 shadow-sm h-100 text-center">
              <Card.Body className="py-4">
                <div className="mb-3">
                  <i className="bi bi-people-fill fs-1 text-success"></i>
                </div>
                <Card.Title className="fs-4 fw-bold">{stats.users}</Card.Title>
                <Card.Text className="text-muted">Total Users</Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col md={2} sm={4} xs={6}>
            <Card className="border-0 shadow-sm h-100 text-center">
              <Card.Body className="py-4">
                <div className="mb-3">
                  <i className="bi bi-file-earmark-text-fill fs-1 text-primary"></i>
                </div>
                <Card.Title className="fs-4 fw-bold">{stats.documents}</Card.Title>
                <Card.Text className="text-muted">Documents</Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col md={2} sm={4} xs={6}>
            <Card className="border-0 shadow-sm h-100 text-center">
              <Card.Body className="py-4">
                <div className="mb-3">
                  <i className="bi bi-folder-fill fs-1 text-warning"></i>
                </div>
                <Card.Title className="fs-4 fw-bold">{stats.categories}</Card.Title>
                <Card.Text className="text-muted">Categories</Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col md={2} sm={4} xs={6}>
            <Card className="border-0 shadow-sm h-100 text-center">
              <Card.Body className="py-4">
                <div className="mb-3">
                  <i className="bi bi-images fs-1 text-danger"></i>
                </div>
                <Card.Title className="fs-4 fw-bold">{stats.galleryItems}</Card.Title>
                <Card.Text className="text-muted">Gallery Items</Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col md={2} sm={4} xs={6}>
            <Card className="border-0 shadow-sm h-100 text-center">
              <Card.Body className="py-4">
                <div className="mb-3">
                  <i className="bi bi-building fs-1 text-info"></i>
                </div>
                <Card.Title className="fs-4 fw-bold">{stats.universities}</Card.Title>
                <Card.Text className="text-muted">Universities</Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col md={2} sm={4} xs={6}>
            <Card className="border-0 shadow-sm h-100 text-center">
              <Card.Body className="py-4">
                <div className="mb-3">
                  <i className="bi bi-people fs-1 text-success"></i>
                </div>
                <Card.Title className="fs-4 fw-bold">{totalCommittee}</Card.Title>
                <Card.Text className="text-muted">Total Committee</Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Main Content */}
        <Row className="g-4">
          {/* Committees Section */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <Card.Title className="mb-0">
                    <i className="bi bi-people me-2 text-success"></i>
                    Committees Distribution
                  </Card.Title>
                  <Badge bg="success" pill className="fs-6">
                    {totalCommittee} Total
                  </Badge>
                </div>

                {committeeData.map((committee, index) => (
                  <div key={index} className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-medium">{committee.name}</span>
                      <span className="text-muted">{committee.value} members</span>
                    </div>
                    <ProgressBar
                      now={totalCommittee ? (committee.value / totalCommittee) * 100 : 0}
                      variant={committee.variant}
                      className="mb-3"
                      style={{ height: '10px', borderRadius: '5px' }}
                    />
                    <div className="text-end">
                      <small className="text-muted">
                        {totalCommittee ? Math.round((committee.value / totalCommittee) * 100) : 0}%
                      </small>
                    </div>
                  </div>
                ))}

                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    State: {stats.stateCommittee}, Unit: {stats.unitCommittee}
                  </small>
                  <Button variant="outline-success" size="sm">
                    View Details
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Leadership Section */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <Card.Title className="mb-4">
                  <i className="bi bi-person-badge me-2 text-success"></i>
                  Leadership Positions
                </Card.Title>

                <Row>
                  <Col md={6} className="mb-3">
                    <Card className="border h-100">
                      <Card.Body className="text-center">
                        <div className="mb-3">
                          <i className="bi bi-person-badge-fill fs-1 text-success"></i>
                        </div>
                        <Card.Title>President</Card.Title>
                        <Card.Text className="fs-3 fw-bold">{stats.president}</Card.Text>
                        <small className="text-muted">Active Position(s)</small>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Card className="border h-100">
                      <Card.Body className="text-center">
                        <div className="mb-3">
                          <i className="bi bi-person-lines-fill fs-1 text-info"></i>
                        </div>
                        <Card.Title>Secretary</Card.Title>
                        <Card.Text className="fs-3 fw-bold">{stats.secretary}</Card.Text>
                        <small className="text-muted">Active Position(s)</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <div className="text-center mt-3">
                  <Button variant="success" size="sm" className="px-4">
                    <i className="bi bi-people-fill me-1"></i>
                    Manage Leadership
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Universities Section */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <Card.Title className="mb-0">
                    <i className="bi bi-building me-2 text-success"></i>
                    Universities Overview
                  </Card.Title>
                  <Badge bg="success" pill className="fs-6">
                    {stats.universities} Total
                  </Badge>
                </div>

                <div className="text-center py-4">
                  <div className="mb-4">
                    <i className="bi bi-globe-americas display-1 text-success"></i>
                  </div>
                  <div className="row text-center">
                    <div className="col-6">
                      <div className="border-end">
                        <div className="fs-3 fw-bold text-success">{stats.universities}</div>
                        <div className="text-muted">Registered</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="fs-3 fw-bold text-success">{stats.universities}</div>
                      <div className="text-muted">Active</div>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                  <Button variant="outline-success" size="sm">
                    <i className="bi bi-gear me-1"></i>
                    Manage
                  </Button>
                  <Button variant="success" size="sm">
                    <i className="bi bi-plus-circle me-1"></i>
                    Add University
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* System Status */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <Card.Title className="mb-4">
                  <i className="bi bi-activity me-2 text-success"></i>
                  System Status & Summary
                </Card.Title>

                <div className="system-status mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                    <span>API Status</span>
                    <Badge bg="success">Healthy</Badge>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                    <span>Data Loaded</span>
                    <Badge bg="success">Success</Badge>
                  </div>
                  <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                    <span>Last Updated</span>
                    <span className="text-muted">{stats.lastUpdated?.toLocaleTimeString()}</span>
                  </div>
                </div>

                <div className="summary-stats">
                  <h6 className="text-muted mb-3">Quick Summary</h6>
                  <Row>
                    <Col xs={6} className="mb-3">
                      <div className="border p-3 rounded text-center">
                        <div className="fw-bold text-success fs-4">{stats.documents}</div>
                        <small className="text-muted">Documents</small>
                      </div>
                    </Col>
                    <Col xs={6} className="mb-3">
                      <div className="border p-3 rounded text-center">
                        <div className="fw-bold text-success fs-4">{stats.galleryItems}</div>
                        <small className="text-muted">Gallery Items</small>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="border p-3 rounded text-center">
                        <div className="fw-bold text-success fs-4">{stats.categories}</div>
                        <small className="text-muted">Categories</small>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="border p-3 rounded text-center">
                        <div className="fw-bold text-success fs-4">{stats.universities}</div>
                        <small className="text-muted">Universities</small>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="text-center mt-4">
                  <Button variant="success" size="sm" className="px-4">
                    <i className="bi bi-download me-1"></i>
                    Export Report
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Quick Actions */}
        <Row className="mt-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <Card.Title className="mb-4">
                  <i className="bi bi-lightning-fill me-2 text-success"></i>
                  Quick Actions
                </Card.Title>
                <Row>
                  {[
                    { title: 'Add User', icon: 'person-plus', variant: 'outline-success' },
                    { title: 'Upload Document', icon: 'cloud-upload', variant: 'outline-success' },
                    { title: 'Manage Committee', icon: 'people-fill', variant: 'outline-success' },
                    { title: 'Add Gallery Item', icon: 'image', variant: 'outline-success' }
                  ].map((action, index) => (
                    <Col md={3} sm={6} key={index} className="mb-3">
                      <Button
                        variant={action.variant}
                        className="w-100 py-3 d-flex flex-column align-items-center"
                      >
                        <i className={`bi bi-${action.icon} fs-3 mb-2`}></i>
                        <span>{action.title}</span>
                      </Button>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserDashboard;